import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Badge } from "primereact/badge";
import { toastify } from "../../../../utils/toast";
import { getItem } from "../../../../utils/localStorage";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { 
  editPropertyManagerContractURL,
  getUsersURL,
  getGLAccountsURL,
  GET_DEPARTMENT_URL
} from "../../../../utils/urls";

const EditPropertyManagerContractDialog = ({ 
  visible, 
  onHide, 
  contractData, 
  onEditSuccess
}) => {
  // Core editable fields
  const [contractName, setContractName] = useState("");
  const [selectedPropertyManager, setSelectedPropertyManager] = useState("");
  const [managementFeeType, setManagementFeeType] = useState("percentage");
  const [managementFeeValue, setManagementFeeValue] = useState("");
  // Optional per-contract lease commission override (empty = facility default applies)
  const [leaseFeeType, setLeaseFeeType] = useState("percentage");
  const [leaseFeeValue, setLeaseFeeValue] = useState("");
  const [status, setStatus] = useState("Inactive");
  const [editReason, setEditReason] = useState("");

  // Readonly display fields - Updated to match actual schema
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [paymentDueDate, setPaymentDueDate] = useState(""); // Payment due date (1-31)
  const [nextInvoiceDate, setNextInvoiceDate] = useState(""); // Next invoice date
  const [balanceBroughtForward, setBalanceBroughtForward] = useState("0.00");
  const [frequency, setFrequency] = useState("Monthly"); // Payment frequency

  // GL Account fields
  const [invoiceDebitAccount, setInvoiceDebitAccount] = useState("");
  const [invoiceCreditAccount, setInvoiceCreditAccount] = useState("");
  const [paymentDebitAccount, setPaymentDebitAccount] = useState("");
  const [paymentCreditAccount, setPaymentCreditAccount] = useState("");

  // Data lists
  const [propertyManagers, setPropertyManagers] = useState([]);
  const [glAccounts, setGlAccounts] = useState([]);
  const [departments, setDepartments] = useState([]);
  
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Options
  const managementFeeTypes = [
    { value: "percentage", label: "Percentage of Rent" },
    { value: "amount", label: "Fixed Amount" }
  ];

  const statusOptions = ['Active', 'Inactive', 'Completed', 'Suspended'];

  // Fetch departments first
  const fetchDepartments = async () => {
    try {
      const facilityId = await getItem("selectedFacilityId");
      const response = await makeRequest2(`${GET_DEPARTMENT_URL}/${facilityId}`, 'GET');
      
      if (response.success && response.data) {
        const departmentData = response.data.data || response.data;
        const departmentsArray = Array.isArray(departmentData) 
          ? departmentData 
          : [departmentData];
        setDepartments(departmentsArray);
        return departmentsArray;
      } else {
        setDepartments([]);
        return [];
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
      setDepartments([]);
      return [];
    }
  };

  // Enhanced fetch property managers with proper department filtering
  const fetchPropertyManagers = async () => {
    try {
      const facilityId = await getItem("selectedFacilityId");
      
      // Fetch users
      const response = await makeRequest2(`${getUsersURL}/${facilityId}`, "GET");
      
      let usersData = [];
      if (response?.success && Array.isArray(response.data)) {
        usersData = response.data;
      } else if (response?.success && response.data && Array.isArray(response.data.users)) {
        usersData = response.data.users;
      } else if (Array.isArray(response)) {
        usersData = response;
      }
      
      console.log('All users data:', usersData);
      
      // Filter users from Property Management department
      // Handle both populated and non-populated department references
      let propManagers = usersData.filter(user => {
        if (!user || !user.department || user.role !== "Staff") return false;
        
        // If department is populated (object with name property)
        if (typeof user.department === 'object' && user.department.name) {
          return user.department.name === 'Property Management';
        }
        
        // If department is just a string ID, we need to check differently
        return false;
      });
      
      console.log('Property managers with populated department:', propManagers);
      
      // If no managers found with populated department, try fetching departments
      // and filter by department ID
      if (propManagers.length === 0) {
        const departmentsData = await fetchDepartments();
        
        // Find Property Management department ID
        const propMgmtDept = departmentsData.find(dept => dept.name === 'Property Management');
        
        if (propMgmtDept) {
          console.log('Property Management department found:', propMgmtDept);
          
          // Filter users by department ID
          propManagers = usersData.filter(user => {
            if (!user || !user.department || user.role !== "Staff") return false;
            
            // Handle department as ID string
            if (typeof user.department === 'string') {
              return user.department === propMgmtDept._id;
            }
            
            // Handle department as object with _id
            if (typeof user.department === 'object' && user.department._id) {
              return user.department._id === propMgmtDept._id;
            }
            
            return false;
          });
          
          console.log('Property managers found by department ID:', propManagers);
        } else {
          console.log('Property Management department not found');
        }
      }
      
      // Add current property manager if not in list and exists
      if (contractData?.propertyManager) {
        const currentPMId = extractPropertyManagerId(contractData.propertyManager);
        const currentPMExists = propManagers.find(pm => pm._id === currentPMId);
        
        if (!currentPMExists && contractData.propertyManagerDetails) {
          // Add current property manager to the list
          propManagers.unshift({
            _id: currentPMId,
            fullName: contractData.propertyManagerDetails.fullName,
            email: contractData.propertyManagerDetails.email,
            role: contractData.propertyManagerDetails.role,
            department: contractData.propertyManagerDetails.department
          });
        }
      }
      
      // If still no property managers found, show all Staff users as fallback
      if (propManagers.length === 0) {
        console.log('No Property Management users found, showing all Staff users as fallback');
        propManagers = usersData.filter(user => 
          user && 
          user.role === "Staff" && 
          user.facilityId === facilityId &&
          user.isEnabled !== false
        );
      }
      
      setPropertyManagers(propManagers);
      
    } catch (error) {
      console.error("Error fetching property managers:", error);
      setPropertyManagers([]);
    }
  };

  // Fetch GL accounts
  const fetchGLAccounts = async () => {
    try {
      const facilityId = await getItem("selectedFacilityId");
      const response = await makeRequest2(`${getGLAccountsURL}/${facilityId}`, "GET");
      if (response?.success) {
        setGlAccounts(response.data?.data || []);
      }
    } catch (error) {
      console.error("Error fetching GL accounts:", error);
      setGlAccounts([]);
    }
  };

  // Extract property manager ID from different formats
  const extractPropertyManagerId = (propertyManager) => {
    if (!propertyManager) return '';
    if (typeof propertyManager === 'string') return propertyManager;
    if (propertyManager._id) return propertyManager._id;
    if (propertyManager.$oid) return propertyManager.$oid;
    return '';
  };

  // Initialize form when contract data changes - Updated to include all schema fields
  useEffect(() => {
    if (contractData && visible) {
      // Editable fields
      setContractName(contractData.contractName || '');
      setSelectedPropertyManager(extractPropertyManagerId(contractData.propertyManager));
      setManagementFeeType(contractData.managementFee?.type || 'percentage');
      setManagementFeeValue(contractData.managementFee?.value?.toString() || '');
      setLeaseFeeType(contractData.leaseFee?.type || 'percentage');
      setLeaseFeeValue(contractData.leaseFee?.value != null ? contractData.leaseFee.value.toString() : '');
      setStatus(contractData.status || 'Inactive');

      // Readonly fields - Updated to include all schema fields
      setStartDate(contractData.startDate ? new Date(contractData.startDate).toISOString().split('T')[0] : '');
      setEndDate(contractData.endDate ? new Date(contractData.endDate).toISOString().split('T')[0] : '');
      setPaymentDueDate(contractData.paymentDueDate?.toString() || ''); // Payment due date (1-31)
      setNextInvoiceDate(contractData.nextInvoiceDate ? new Date(contractData.nextInvoiceDate).toISOString().split('T')[0] : ''); // Next invoice date
      setBalanceBroughtForward(contractData.balanceBroughtForward?.toString() || '0.00');
      setFrequency(contractData.frequency || 'Monthly');

      // Set GL accounts
      if (contractData.glAccounts) {
        setInvoiceDebitAccount(contractData.glAccounts.invoice?.debit || '');
        setInvoiceCreditAccount(contractData.glAccounts.invoice?.credit || '');
        setPaymentDebitAccount(contractData.glAccounts.payment?.debit || '');
        setPaymentCreditAccount(contractData.glAccounts.payment?.credit || '');
      }

      setEditReason('');
      setErrors({});

      // Fetch data
      fetchPropertyManagers();
      fetchGLAccounts();
    }
  }, [contractData, visible]);

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!contractName?.trim()) {
      newErrors.contractName = 'Contract name is required';
    }

    if (!selectedPropertyManager) {
      newErrors.propertyManager = 'Property manager is required';
    }

    if (!managementFeeValue || isNaN(managementFeeValue) || managementFeeValue < 0) {
      newErrors.managementFeeValue = 'Management fee value must be a non-negative number';
    }

    if (managementFeeType === 'percentage' && managementFeeValue > 100) {
      newErrors.managementFeeValue = 'Percentage cannot exceed 100%';
    }

    if (!leaseFeeValue || isNaN(leaseFeeValue) || leaseFeeValue < 0) {
      newErrors.leaseFeeValue = 'Lease commission value must be a non-negative number';
    }

    if (leaseFeeType === 'percentage' && leaseFeeValue > 100) {
      newErrors.leaseFeeValue = 'Percentage cannot exceed 100%';
    }

    if (!editReason?.trim()) {
      newErrors.editReason = 'Edit reason is required for audit trail';
    } else if (editReason.trim().length < 10) {
      newErrors.editReason = 'Edit reason must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      toastify("Please fix the form errors", "error");
      return;
    }

    try {
      setLoading(true);
      const facilityId = await getItem("selectedFacilityId");
      
      const requestBody = {
        contractName: contractName.trim(),
        propertyManager: selectedPropertyManager,
        managementFee: {
          type: managementFeeType,
          value: parseFloat(managementFeeValue)
        },
        leaseFee: leaseFeeValue !== ""
          ? { type: leaseFeeType, value: parseFloat(leaseFeeValue) }
          : null,
        status: status,
        editReason: editReason.trim()
      };

      // Add GL accounts if all fields are filled
      if (invoiceDebitAccount && invoiceCreditAccount && paymentDebitAccount && paymentCreditAccount) {
        requestBody.glAccounts = {
          invoice: {
            debit: invoiceDebitAccount,
            credit: invoiceCreditAccount
          },
          payment: {
            debit: paymentDebitAccount,
            credit: paymentCreditAccount
          }
        };
      }

      console.log('Submitting edit request:', requestBody);

      const response = await makeRequest2(
        `${editPropertyManagerContractURL}/${facilityId}/${contractData._id}`, 
        'PUT',
        requestBody
      );

      console.log('Edit response:', response);

      if (response?.success) {
        toastify("Contract updated successfully", "success");
        if (onEditSuccess) onEditSuccess(response.data);
        onHide();
      } else {
        const errorMessage = response?.error || "Failed to update contract";
        toastify(errorMessage, "error");
      }
    } catch (error) {
      console.error('Error editing contract:', error);
      toastify("Error updating contract: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  // Get display names
  const getPropertyManagerName = () => {
    if (!selectedPropertyManager) return "No property manager selected";
    
    const manager = propertyManagers.find(pm => pm._id === selectedPropertyManager);
    if (manager) {
      return manager.fullName || `${manager.firstName || ''} ${manager.lastName || ''}`.trim() || manager.email;
    }
    
    if (contractData?.propertyManagerDetails?.fullName) {
      return contractData.propertyManagerDetails.fullName;
    }
    
    return "Property manager details loading...";
  };

  const getUnitName = () => {
    if (contractData?.unitDetails?.[0]) {
      const unit = contractData.unitDetails[0];
      return `${unit.name} - ${unit.unitType} (${unit.division})`;
    }
    return "Apartment information not available";
  };

  const getClientName = () => {
    if (contractData?.customer) {
      return `${contractData.customer.firstName} ${contractData.customer.lastName}`;
    }
    return "Client information not available";
  };

  // Check if we have lease data - Updated to use actual schema field names
  const hasLeaseData = contractData && contractData.startDate && contractData.endDate && contractData.paymentDueDate;

  // Dialog footer
  const dialogFooter = (
    <div className="d-flex justify-content-end gap-2">
      <Button 
        label="Cancel" 
        icon="ti ti-x" 
        onClick={onHide} 
        className="p-button-secondary"
        disabled={loading}
      />
      <Button 
        label={loading ? "Saving..." : "Save Changes"} 
        icon={loading ? "ti ti-loader" : "ti ti-check"}
        onClick={handleSubmit} 
        className="p-button-primary"
        disabled={loading}
        loading={loading}
      />
    </div>
  );

  return (
    <Dialog
      visible={visible}
      onHide={!loading ? onHide : undefined}
      header="Edit Property Manager Contract"
      footer={dialogFooter}
      style={{ width: '90vw', maxWidth: '900px' }}
      modal
      closable={!loading}
    >
      <div style={{ maxHeight: '75vh', overflowY: 'auto' }}>
        
        {/* Status Alert */}
        {hasLeaseData && (
          <div className="alert alert-success mb-4">
            <i className="ti ti-check me-2"></i>
            <strong>Lease Data Found:</strong> This apartment has an existing lease agreement. Some fields are automatically populated and cannot be edited here.
          </div>
        )}
        
        {!hasLeaseData && (
          <div className="alert alert-warning mb-4">
            <i className="ti ti-alert-triangle me-2"></i>
            <strong>No Lease Data:</strong> This apartment does not have an existing lease agreement. Contract fields will remain as manually set.
          </div>
        )}

        {/* Basic Information */}
        <div className="card mb-4">
          <div className="card-header">
            <h6 className="mb-0">Basic Information</h6>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Contract Name <span className="text-danger">*</span></label>
                <InputText
                  className={`w-100 ${errors.contractName ? 'p-invalid' : ''}`}
                  value={contractName}
                  onChange={(e) => setContractName(e.target.value)}
                  placeholder="Enter contract name"
                  disabled={loading}
                />
                {errors.contractName && (
                  <small className="text-danger">{errors.contractName}</small>
                )}
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Property Manager <span className="text-danger">*</span></label>
                <select
                  className={`form-control ${errors.propertyManager ? 'is-invalid' : ''}`}
                  value={selectedPropertyManager}
                  onChange={(e) => setSelectedPropertyManager(e.target.value)}
                  disabled={loading}
                >
                  <option value="">-- Select Property Manager --</option>
                  {propertyManagers.map((manager) => (
                    <option key={manager._id} value={manager._id}>
                      {manager.fullName || `${manager.firstName || ''} ${manager.lastName || ''}`.trim()} - {manager.email}
                    </option>
                  ))}
                </select>
                {errors.propertyManager && (
                  <small className="text-danger">{errors.propertyManager}</small>
                )}
                <small className="text-muted">
                  {propertyManagers.length === 0 ? (
                    <span className="text-warning">
                      <i className="ti ti-alert-triangle me-1"></i>
                      No property managers found. Ensure users are assigned to "Property Management" department.
                    </span>
                  ) : (
                    `Select a property manager from the Property Management department (${propertyManagers.length} available)`
                  )}
                </small>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Status</label>
                <select
                  className="form-control"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  disabled={loading}
                >
                  {statusOptions.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* apartment & Client (Read-only) */}
        <div className="card mb-4">
          <div className="card-header">
            <h6 className="mb-0">Apartment & Client Selection</h6>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Apartment to Manage (Cannot be changed)</label>
                <div className="form-control bg-light text-muted">
                  <i className="ti ti-lock me-2"></i>
                  {getUnitName()}
                </div>
                <small className="text-info">
                  <i className="ti ti-info-circle me-1"></i>
                  Apartment cannot be changed when editing a contract
                </small>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Client (Cannot be changed)</label>
                <div className="form-control bg-light text-muted">
                  <i className="ti ti-lock me-2"></i>
                  {getClientName()}
                </div>
                <small className="text-info">
                  <i className="ti ti-info-circle me-1"></i>
                  Client is automatically selected based on apartment ownership and cannot be changed
                </small>
              </div>
            </div>
          </div>
        </div>

        {/* Management Fee */}
        <div className="card mb-4">
          <div className="card-header">
            <h6 className="mb-0">Management Fee Configuration</h6>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Management Fee Type <span className="text-danger">*</span></label>
                <select
                  className="form-control"
                  value={managementFeeType}
                  onChange={(e) => setManagementFeeType(e.target.value)}
                  disabled={loading}
                >
                  {managementFeeTypes.map((type) => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Management Fee Value <span className="text-danger">*</span></label>
                <div className="input-group">
                  <input
                    type="number"
                    className={`form-control ${errors.managementFeeValue ? 'is-invalid' : ''}`}
                    value={managementFeeValue}
                    onChange={(e) => setManagementFeeValue(e.target.value)}
                    min="0"
                    max={managementFeeType === "percentage" ? "100" : undefined}
                    step="0.01"
                    placeholder={managementFeeType === "percentage" ? "Enter percentage (0-100)" : "Enter fixed amount"}
                    disabled={loading}
                  />
                  <span className="input-group-text">
                    {managementFeeType === "percentage" ? "%" : "$"}
                  </span>
                </div>
                {errors.managementFeeValue && (
                  <small className="text-danger">{errors.managementFeeValue}</small>
                )}
                <small className="text-muted">
                  {managementFeeType === "percentage"
                    ? "Percentage of rent to be charged as management fee"
                    : "Fixed amount to be charged as management fee"
                  }
                </small>
              </div>
            </div>

            <hr className="my-3" />

            <h6 className="mb-1">Lease Commission</h6>
            <p className="text-muted small mb-3">
              The one-time fee earned when this manager places a new tenant on
              a unit covered by this contract, paid on the first rent payment.
            </p>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Lease Commission Type <span className="text-danger">*</span></label>
                <select
                  className="form-select"
                  value={leaseFeeType}
                  onChange={(e) => setLeaseFeeType(e.target.value)}
                >
                  {managementFeeTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-6 mb-3">
                <label className="form-label">Lease Commission Value <span className="text-danger">*</span></label>
                <div className="input-group">
                  <input
                    type="number"
                    className={`form-control ${errors.leaseFeeValue ? 'is-invalid' : ''}`}
                    value={leaseFeeValue}
                    onChange={(e) => setLeaseFeeValue(e.target.value)}
                    min="0"
                    max={leaseFeeType === "percentage" ? "100" : undefined}
                    step="0.01"
                    placeholder={leaseFeeType === "percentage" ? "Enter percentage (0-100)" : "Enter fixed amount"}
                  />
                  <span className="input-group-text">
                    {leaseFeeType === "percentage" ? "%" : "KES"}
                  </span>
                </div>
                {errors.leaseFeeValue && (
                  <small className="text-danger">{errors.leaseFeeValue}</small>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Contract Terms (Read-only) - Updated to include all schema fields */}
        <div className="card mb-4">
          <div className="card-header">
            <h6 className="mb-0">Contract Terms (Lease-Synced Fields)</h6>
            {hasLeaseData ? (
              <small className="text-success">
                <i className="ti ti-check me-1"></i>
                These fields are automatically populated from existing lease data
              </small>
            ) : (
              <small className="text-warning">
                <i className="ti ti-alert-triangle me-1"></i>
                These fields will be populated automatically once a lease agreement is created for this apartment
              </small>
            )}
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Start Date</label>
                <input
                  type="date"
                  className="form-control bg-light"
                  value={startDate}
                  disabled={true}
                />
                <small className="text-muted">
                  <i className="ti ti-lock me-1"></i>
                  {hasLeaseData ? 'Populated from lease agreement' : 'Will be populated when lease agreement is created'}
                </small>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">End Date</label>
                <input
                  type="date"
                  className="form-control bg-light"
                  value={endDate}
                  disabled={true}
                />
                <small className="text-muted">
                  <i className="ti ti-lock me-1"></i>
                  {hasLeaseData ? 'Populated from lease agreement' : 'Will be populated when lease agreement is created'}
                </small>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Payment Due Date</label>
                <input
                  type="text"
                  className="form-control bg-light"
                  value={paymentDueDate ? `${paymentDueDate}${paymentDueDate === '1' ? 'st' : paymentDueDate === '2' ? 'nd' : paymentDueDate === '3' ? 'rd' : 'th'} of month` : ''}
                  disabled={true}
                />
                <small className="text-muted">
                  <i className="ti ti-lock me-1"></i>
                  {hasLeaseData ? 'Populated from lease payment terms' : 'Will be populated when lease agreement is created'}
                </small>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Payment Frequency</label>
                <input
                  type="text"
                  className="form-control bg-light"
                  value={frequency}
                  disabled={true}
                />
                <small className="text-muted">
                  <i className="ti ti-lock me-1"></i>
                  {hasLeaseData ? 'Populated from lease billing cycle' : 'Will be populated when lease agreement is created'}
                </small>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Next Invoice Date</label>
                <input
                  type="date"
                  className="form-control bg-light"
                  value={nextInvoiceDate}
                  disabled={true}
                />
                <small className="text-muted">
                  <i className="ti ti-lock me-1"></i>
                  {hasLeaseData ? 'Calculated based on lease billing cycle' : 'Will be calculated when lease agreement is created'}
                </small>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Balance Brought Forward</label>
                <input
                  type="text"
                  className="form-control bg-light"
                  value={balanceBroughtForward}
                  disabled={true}
                />
                <small className="text-muted">
                  <i className="ti ti-lock me-1"></i>
                  {hasLeaseData ? 'Populated from lease financial terms' : 'Will be populated when lease agreement is created'}
                </small>
              </div>
            </div>
          </div>
        </div>

        {/* GL Accounts */}
        {glAccounts.length > 0 && (
          <div className="card mb-4">
            <div className="card-header">
              <h6 className="mb-0">GL Account Configuration</h6>
              <small className="text-info">
                <i className="ti ti-info-circle me-1"></i>
                GL accounts must be manually selected for property management contracts
              </small>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-12 mb-2">
                  <h6>Invoice GL Entries</h6>
                  <p className="text-muted mb-3">Configure accounts for invoice generation</p>
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Invoice Debit Account</label>
                  <select
                    className="form-control"
                    value={invoiceDebitAccount}
                    onChange={(e) => setInvoiceDebitAccount(e.target.value)}
                    disabled={loading}
                  >
                    <option value="">-- Select Debit Account --</option>
                    {glAccounts.map((account) => (
                      <option key={account._id} value={account._id}>
                        {account.accountCode} - {account.accountName} ({account.accountType})
                      </option>
                    ))}
                  </select>
                  <small className="text-muted">Account to be debited when invoices are created</small>
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Invoice Credit Account</label>
                  <select
                    className="form-control"
                    value={invoiceCreditAccount}
                    onChange={(e) => setInvoiceCreditAccount(e.target.value)}
                    disabled={loading}
                  >
                    <option value="">-- Select Credit Account --</option>
                    {glAccounts.map((account) => (
                      <option key={account._id} value={account._id}>
                        {account.accountCode} - {account.accountName} ({account.accountType})
                      </option>
                    ))}
                  </select>
                  <small className="text-muted">Account to be credited when invoices are created</small>
                </div>

                <div className="col-12 mb-2">
                  <h6>Payment GL Entries</h6>
                  <p className="text-muted mb-3">Configure accounts for payment processing</p>
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Payment Debit Account</label>
                  <select
                    className="form-control"
                    value={paymentDebitAccount}
                    onChange={(e) => setPaymentDebitAccount(e.target.value)}
                    disabled={loading}
                  >
                    <option value="">-- Select Debit Account --</option>
                    {glAccounts.map((account) => (
                      <option key={account._id} value={account._id}>
                        {account.accountCode} - {account.accountName} ({account.accountType})
                      </option>
                    ))}
                  </select>
                  <small className="text-muted">Account to be debited when payments are received</small>
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Payment Credit Account</label>
                  <select
                    className="form-control"
                    value={paymentCreditAccount}
                    onChange={(e) => setPaymentCreditAccount(e.target.value)}
                    disabled={loading}
                  >
                    <option value="">-- Select Credit Account --</option>
                    {glAccounts.map((account) => (
                      <option key={account._id} value={account._id}>
                        {account.accountCode} - {account.accountName} ({account.accountType})
                      </option>
                    ))}
                  </select>
                  <small className="text-muted">Account to be credited when payments are received</small>
                </div>
              </div>
            </div>
          </div>
        )}

        {glAccounts.length === 0 && (
          <div className="card mb-4">
            <div className="card-header">
              <h6 className="mb-0">GL Account Configuration</h6>
            </div>
            <div className="card-body">
              <div className="alert alert-warning">
                <i className="ti ti-alert-circle me-2"></i>
                No GL accounts found. GL accounts are optional but recommended for proper accounting.
              </div>
            </div>
          </div>
        )}

        {/* Edit Reason */}
        <div className="card mb-4">
          <div className="card-header">
            <h6 className="mb-0">Edit Reason</h6>
          </div>
          <div className="card-body">
            <div className="form-group">
              <label htmlFor="editReason" className="form-label">
                Reason for Edit <span className="text-danger">*</span>
              </label>
              <InputTextarea
                id="editReason"
                value={editReason}
                onChange={(e) => setEditReason(e.target.value)}
                placeholder="Please provide a reason for this edit for audit trail purposes (minimum 10 characters)"
                rows={3}
                className={`w-100 ${errors.editReason ? 'p-invalid' : ''}`}
                disabled={loading}
                maxLength={500}
              />
              <div className="d-flex justify-content-between align-items-center mt-1">
                {errors.editReason && (
                  <small className="text-danger">{errors.editReason}</small>
                )}
                <small className="text-muted ms-auto">{editReason.length}/500 characters</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default EditPropertyManagerContractDialog;